import { NgClass } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { IconComponent } from "@components/icon/icon.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { VirkeskopAffarsformer } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Geometri } from "~app/features/virkeskop/models/karta-models";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { VkKartaMediatorService } from "~app/features/virkeskop/services/vk-karta-mediator.service";

@Component({
    selector: "rask-kontrakt-header-icons",
    templateUrl: "./kontrakt-header-icons.component.html",
    styleUrls: ["./kontrakt-header-icons.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IconComponent,
        NgClass
    ]
})
export class KontraktHeaderIconsComponent implements OnInit, OnDestroy {
    kontrakt: Kontrakt;
    kontraktGeometrier: Geometri[];

    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngOnDestroy$ = new Subject<void>();
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    ngOnInit(): void {
        this.kontraktMediatorService.kontrakt$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(kontrakt => {
                this.kontrakt = kontrakt;
            });

        this.vkKartaMediatorService.kontraktGeometrier$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(kontraktGeometrier => {
                this.kontraktGeometrier = kontraktGeometrier;
            });
    }

    protected getVacsStatusText(): string {
        if (!this.kontrakt || !this.kontrakt?.skickadTillVacs) {
            return "Ej skickad till VACS";
        }
        if (this.isKontraktVacsValid()) {
            return "Skickad till VACS";
        } else {
            return "Kontraktet är ändrat. Uppdatera VACS";
        }
    }

    protected hasDrivningspunkter(): boolean {
        return ObjectUtils.hasValue(this.kontrakt) && (this.kontrakt?.affarsform === VirkeskopAffarsformer.Rotpost || // U2
            this.kontrakt?.affarsform === VirkeskopAffarsformer.AuFastKostnad || // U4
            this.kontrakt?.affarsform === VirkeskopAffarsformer.AuSjalvkostnad || // U5
            this.kontrakt?.affarsform === VirkeskopAffarsformer.Leveransrotkop); // U6
    }

    protected isAllDrivningspunkterValid(): boolean {
        return this.isLarmkoordinatValid() && this.isKojplatsValid() && this.isAvlaggValid();
    }

    protected isAvlaggValid(): boolean {
        const avlagg = this.kontraktGeometrier?.filter(x => x.isAvlagg);
        return avlagg?.length > 0 &&
               avlagg.every(a => a.kartgeometri?.tillganglighetsklass?.length > 0);
    }

    protected isKojplatsValid(): boolean {
        return this.kontraktGeometrier?.some(x => x.isKojplats);
    }

    protected isKontrakt(): boolean {
        return this.kontrakt?.isKontrakt;
    }

    protected isKontraktVacsValid(): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (!this.kontrakt || !this.kontrakt?.skickadTillVacs) {
            return false;
        }
        return true;
    }

    protected isLarmkoordinatValid(): boolean {
        return this.kontraktGeometrier?.filter(x => x.isLarmpunkt)?.length >= 1;
    }
}
